import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import { useCompany, useCompanyStore } from "hooks";
import { Company, CompanyForm, CompanyQuery } from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import { Plus } from "lucide-react";

const Companies: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<Company | null>(null);
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [queryParams, setQueryParams] = useState<CompanyQuery>({
    page: 1,
    pageSize: 20,
  });
  const [showInlineAddRow, setShowInlineAddRow] = useState<any>('');
  const [confirmRow, setConfirmRow] = useState<Company | null>(null);
  const {
    rows,
    setRows,
    upsertRow,
    removeRow,
    editingId,
    setEditingId,
    isLoading,
    setLoading,
  } = useCompanyStore();
  const { fetchList, createCompany, updateCompany, deleteCompany } =
    useCompany();

  const {
    data: companyData,
    isFetching,
    refetch,
  } = fetchList(queryParams, true);

  useEffect(() => {
    if (companyData?.data) {
      setRows(companyData.data as Company[]);
    }
  }, [companyData, setRows, isFetching]);

  useEffect(() => {
    setLoading(
      Boolean(
        isFetching ||
        createCompany.isPending ||
        updateCompany.isPending ||
        deleteCompany.isPending,
      ),
    );
  }, [
    isFetching,
    createCompany.isPending,
    updateCompany.isPending,
    deleteCompany.isPending,
    setLoading,
  ]);

  const handleDeleteRow = useCallback((row: Company) => {
    setConfirmRow(row);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteCompany.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        if (editingId === confirmRow.id) {
          setEditingId(null);
        }
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa công ty không thành công");
      },
    });
  }, [confirmRow, deleteCompany, editingId, removeRow, setEditingId]);

  const columns = useMemo<ColDef<Company>[]>(() => {
    return [
      {
        headerName: "Tên công ty",
        field: "business_name",
        flex: 1,
        minWidth: 180,

      },
      {
        headerName: "Email",
        field: "email",
        minWidth: 180,

      },
      {
        headerName: "Số điện thoại",
        field: "phone",
        minWidth: 150,

      },
      {
        headerName: "Địa chỉ",
        field: "address",
        flex: 1,
        minWidth: 200,

      },
      {
        headerName: "Mã số thuế",
        field: "tax_code",
        minWidth: 160,

      },
      // {
      //   headerName: "Mô tả",
      //   field: "description",
      //   flex: 1,
      //   minWidth: 220,
      //   
      // },
    ];
  }, []);

  const normalizePayload = useCallback(
    (row: Partial<Company>): CompanyForm => ({
      business_name: String(row.business_name ?? "").trim(),
      email: String(row.email ?? "").trim(),
      phone: String(row.phone ?? "").trim(),
      address: String(row.address ?? "").trim(),
      tax_code: String(row.tax_code ?? "").trim(),
      description: row.description ? String(row.description) : "",
    }),
    [],
  );

  type PersistMeta = {
    pinned?: boolean;
    onRevert?: () => void;
    onSuccess?: () => void;
    refetch?: boolean;
  };

  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      business_name: "",
      email: "",
      phone: "",
      address: "",
      tax_code: "",
      description: "",
    } as Company;
    setAddRowVersion((v) => v + 1);
  }, []);

  const persistRow = useCallback(
    (row: Partial<Company>, meta?: PersistMeta) => {
      const payload = normalizePayload(row);

      if (!payload.business_name) {
        SnackbarUtils.error("Tên công ty là bắt buộc");
        meta?.onRevert?.();
        return;
      }

      const shouldCreate = meta?.pinned || !row.id;
      if (shouldCreate) {
        createCompany.mutate(payload, {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as Company);
            }
            resetInlineAddRow();
            if (meta?.refetch !== false) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Thêm công ty không thành công");
            meta?.onRevert?.();
          },
        });
        return;
      }

      if (!row.id) {
        SnackbarUtils.error("Không xác định được công ty cần cập nhật");
        meta?.onRevert?.();
        return;
      }

      updateCompany.mutate(
        { id: row.id, ...payload },
        {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as Company);
            } else {
              upsertRow({ ...(row as Company), ...payload });
            }
            if (meta?.refetch) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Cập nhật công ty không thành công");
            meta?.onRevert?.();
          },
        },
      );
    },
    [
      createCompany,
      normalizePayload,
      refetch,
      resetInlineAddRow,
      updateCompany,
      upsertRow,
    ],
  );

  const handleSaveRow = useCallback(
    (row: Company, meta?: { pinned?: boolean }) => {
      persistRow(row, {
        pinned: meta?.pinned,
        refetch: meta?.pinned || !row.id,
      });
    },
    [persistRow],
  );

  const handleEditRow = useCallback(
    (params) => {
      setShowInlineAddRow('edit')
      setEditingId(params.data.id);
      setTimeout(() => gridApiRef.current?.startEditingCell({
        rowIndex: params.node.rowIndex,
        colKey: "code",
      }), 300);
    },
    [setEditingId],
  );

  const toggleInlineAddRow = useCallback(() => {
    setShowInlineAddRow((prev) => {
      const next = !prev ? 'add' : '';
      if (next) {
        resetInlineAddRow();
      } else {
        addRowRef.current = null;
        setAddRowVersion((v) => v + 1);
      }
      return next;
    });
  }, [resetInlineAddRow]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  const inlineAddRowData = useMemo(
    () => (showInlineAddRow ? (addRowRef.current ?? undefined) : undefined),
    [showInlineAddRow, addRowVersion],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">Quản lý công ty</h1>
        <p className="text-sm text-gray-600">
          Quản lý thông tin công ty: thêm mới, chỉnh sửa nhanh trên bảng và xoá
          khi không còn sử dụng.
        </p>
      </div>


      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách công ty
            </h2>

          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleInlineAddRow}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus className="text-green-700" />
            </button>
          </div>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          showAddRow={showInlineAddRow}
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          totalPages={companyData?.totalPage}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onSave={handleSaveRow}
          getRowId={(params) => String(params.data.id)}
          onAfterActions={() => {
            setShowInlineAddRow('');
            setEditingId(null)
          }}
          onQueryChange={handleQueryChange}
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách công ty, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa công ty?"
        description={`Bạn chắc chắn muốn xóa công ty "${confirmRow?.business_name ?? ""}"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteCompany.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default Companies;
